import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '@/api'
import { formatDate } from '@/utils'
import Calendar from '@/components/Calendar'

export default function AdminAppointments() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  
  // 从 URL 参数读取筛选条件
  const filter = searchParams.get('filter') || 'all'
  const dateParam = searchParams.get('date')
  
  // 初始化：只在第一次加载时检查 URL 参数
  useEffect(() => {
    if (dateParam) {
      const parsedDate = new Date(dateParam + 'T00:00:00')
      setSelectedDate(parsedDate)
    }
  }, [])

  // 当 URL 参数变化时，更新选中日期
  useEffect(() => {
    if (dateParam) {
      const parsedDate = new Date(dateParam + 'T00:00:00')
      setSelectedDate(parsedDate)
    } else {
      setSelectedDate(null)
    }
  }, [dateParam])

  // 使用固定 key，只在 filter 或 date 变化时重新请求
  const { data, mutate, isLoading } = useSWR(
    `appointments-${filter}-${dateParam || 'all'}`,
    () => api.appointments.getAll(filter !== 'all' ? { status: filter } : undefined),
    { 
      refreshInterval: 0,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  )

  const appointments = data?.data || []

  const handleUpdateStatus = async (id: number, status: string) => {
    if (!confirm(`确定将预约状态改为"${status}"吗？`)) return
    try {
      await api.appointments.updateStatus(id, status)
      await mutate(undefined, { revalidate: true })
      alert('✅ 状态已更新')
    } catch (err: any) {
      alert('❌ 更新失败：' + err.message)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除此预约吗？')) return
    try {
      await api.appointments.delete(id)
      await mutate(undefined, { revalidate: true })
      alert('✅ 删除成功')
    } catch (err: any) {
      alert('❌ 删除失败：' + err.message)
    }
  }

  // 按选中日期过滤
  const filteredAppointments = selectedDate
    ? appointments.filter(apt => {
        if (!apt.appointment_date) return false
        const aptDate = new Date(apt.appointment_date + 'T00:00:00')
        const selDate = selectedDate
        return aptDate.getFullYear() === selDate.getFullYear() &&
               aptDate.getMonth() === selDate.getMonth() &&
               aptDate.getDate() === selDate.getDate()
      })
    : appointments

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800',
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '待确认',
      confirmed: '已确认',
      completed: '已完成',
      cancelled: '已取消',
      no_show: '未到场',
    }
    return labels[status] || status
  }

  const handleDateSelect = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateStr = `${year}-${month}-${day}`
    
    setSelectedDate(date)
    setSearchParams({ date: dateStr })
  }

  const clearFilters = () => {
    setSearchParams({})
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">预约管理</h2>
        <div className="text-sm text-gray-600">
          共 {appointments.length} 条预约
        </div>
      </div>

      {/* 提示信息 */}
      {dateParam && (
        <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-xl flex justify-between items-center">
          <p className="text-sm text-primary-800">
            📅 已筛选日期：<strong>{formatDate(dateParam)}</strong>
          </p>
          <button onClick={() => setSearchParams({})} className="text-sm text-primary-600 hover:text-primary-700">
            显示全部 ✕
          </button>
        </div>
      )}

      {/* 日历和筛选 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            appointments={appointments}
            showAppointments={true}
          />
        </div>
        
        {/* 状态筛选 */}
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-3">筛选</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSearchParams({ filter: 'all' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              全部
            </button>
            {['pending', 'confirmed', 'completed', 'cancelled'].map(status => (
              <button
                key={status}
                onClick={() => setSearchParams({ filter: status })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getStatusLabel(status)}
              </button>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm text-gray-600 space-y-1">
              <p>总预约数：<span className="font-semibold">{appointments.length}</span></p>
              <p>待确认：<span className="font-semibold text-yellow-600">{appointments.filter(a => a.status === 'pending').length}</span></p>
              <p>已确认：<span className="font-semibold text-green-600">{appointments.filter(a => a.status === 'confirmed').length}</span></p>
            </div>
          </div>
          
          <button
            onClick={clearFilters}
            className="mt-3 w-full btn-secondary text-sm"
          >
            清除所有筛选
          </button>
        </div>
      </div>

      {/* 预约列表 */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">
          {selectedDate ? `📅 ${formatDate(selectedDate.toISOString())} 的预约` : '全部预约'}
          <span className="text-sm font-normal text-gray-500 ml-2">({filteredAppointments.length}条)</span>
        </h3>
        
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">
            <div className="animate-pulse text-2xl">⏳</div>
            <p className="mt-2">加载中...</p>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>暂无预约记录</p>
            {selectedDate ? (
              <button onClick={clearFilters} className="mt-3 btn-secondary text-sm">
                显示全部预约
              </button>
            ) : (
              <p className="text-sm mt-2">
                <a href="/booking" className="text-primary-600 hover:text-primary-700">去创建预约</a>
              </p>
            )}
          </div>
        ) : (
          filteredAppointments.map(apt => (
            <div key={apt.id} className="card hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{apt.customer_name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(apt.status)}`}>
                      {getStatusLabel(apt.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{apt.customer_phone}</p>
                  {apt.customer_wechat && (
                    <p className="text-xs text-gray-400">微信：{apt.customer_wechat}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(apt.id)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  删除
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3">
                <div>
                  <span className="text-gray-500">服务：</span>
                  <span className="text-gray-900">{apt.service?.name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">美容师：</span>
                  <span className="text-gray-900">{apt.beautician?.name || '未指定'}</span>
                </div>
                <div>
                  <span className="text-gray-500">日期：</span>
                  <span className="text-gray-900">{formatDate(apt.appointment_date)}</span>
                </div>
                <div>
                  <span className="text-gray-500">时间：</span>
                  <span className="text-gray-900">{apt.appointment_time}</span>
                </div>
              </div>

              {apt.notes && (
                <p className="text-sm text-gray-600 mb-3 p-2 bg-gray-50 rounded">
                  📝 备注：{apt.notes}
                </p>
              )}

              {apt.status === 'pending' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdateStatus(apt.id, 'confirmed')}
                    className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    ✓ 确认预约
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(apt.id, 'cancelled')}
                    className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    ✕ 取消
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
